import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Segunda' },
  { value: 2, label: 'Terça' },
  { value: 3, label: 'Quarta' },
  { value: 4, label: 'Quinta' },
  { value: 5, label: 'Sexta' },
  { value: 6, label: 'Sábado' }
];

/**
 * WorkingHoursEditor - Reusable component for editing working hours
 * Uses EmployeesManagementStore directly to avoid code duplication
 *
 * @param {Object} store - EmployeesManagementStore instance
 * @param {Array} defaultWorkingHours - Shop's default working hours (optional)
 */
const WorkingHoursEditor = observer(({ store, defaultWorkingHours = null }) => {
  const { formData } = store;

  // Format default working hours for display
  const formatDefaultHours = () => {
    if (!defaultWorkingHours || defaultWorkingHours.length === 0) {
      return null;
    }

    return defaultWorkingHours.map(wh => {
      const dayLabel = DAYS_OF_WEEK.find(d => d.value === wh.day)?.label || `Dia ${wh.day}`;
      const intervals = wh.intervals.map(int => `${int.start} - ${int.end}`).join(', ');
      return { day: dayLabel, intervals };
    });
  };

  const defaultHoursFormatted = formatDefaultHours();

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          Horários de Trabalho
        </Typography>
        <Button
          startIcon={<AddIcon />}
          onClick={() => store.addWorkingDay()}
          size="small"
          variant="outlined"
        >
          Adicionar Dia
        </Button>
      </Box>

      {formData.workingHours.length === 0 && (
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Nenhum horário específico configurado (usará horários padrão da barbearia)
          </Typography>

          {defaultHoursFormatted && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed', borderColor: 'grey.300' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Horários padrão da barbearia:
              </Typography>
              {defaultHoursFormatted.map((item, index) => (
                <Typography key={index} variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.8 }}>
                  <strong>{item.day}:</strong> {item.intervals}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      {formData.workingHours.map((workingDay, dayIndex) => (
        <Card key={dayIndex} sx={{ mb: 2 }}>
          <CardHeader
            title={
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Dia da Semana</InputLabel>
                <Select
                  value={workingDay.day}
                  onChange={(e) => store.updateWorkingDay(dayIndex, 'day', e.target.value)}
                  label="Dia da Semana"
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
            action={
              <IconButton onClick={() => store.removeWorkingDay(dayIndex)} color="error">
                <DeleteIcon />
              </IconButton>
            }
          />
          <CardContent>
            {workingDay.intervals.map((interval, intervalIndex) => (
              <Box key={intervalIndex} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                  label="Início"
                  type="time"
                  value={interval.start}
                  onChange={(e) => store.updateInterval(dayIndex, intervalIndex, 'start', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                <TextField
                  label="Fim"
                  type="time"
                  value={interval.end}
                  onChange={(e) => store.updateInterval(dayIndex, intervalIndex, 'end', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
                {workingDay.intervals.length > 1 && (
                  <IconButton
                    onClick={() => store.removeInterval(dayIndex, intervalIndex)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={() => store.addInterval(dayIndex)}
              size="small"
            >
              Adicionar Intervalo
            </Button>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
});

export default WorkingHoursEditor;
